/**
 * A card's part in the day view's board (ADR 0194, decisions 3 and 5): its
 * menu, and selecting several. The day view gives each block's card and
 * ticket's card a `CardBoard` (`boardActions`); these are the card's own
 * handlers for it, around its peek's (`usePeek`).
 */
import type { KeyboardEvent, MouseEvent } from "react";

import { isContextMenuKey } from "../../components/ui/useMenu";

/**
 * A card on the day view's board, for its menu and for selecting several
 * (ADR 0194, decisions 3 and 5): the day view's.
 */
export interface CardBoard {
  /** Its key among the selected (`pickKey`). */
  key: string;
  /** It's one of the selected cards. */
  picked: boolean;
  /** A click with Ctrl, ⌘ or Shift, on a desktop: true when it selected, and opens nothing. */
  onPickClick: (event: MouseEvent<HTMLButtonElement>) => boolean;
  /** A right-click on the card: its menu, unless the browser's own stays (a phone, touch, a popup). */
  onContextMenu: (event: MouseEvent<HTMLElement>, button: HTMLButtonElement | null) => void;
  /** ☰ or Shift+F10 on the card: its menu, at the card's corner. */
  onMenuKey: (button: HTMLButtonElement) => void;
}

/**
 * A selected card (ADR 0194, decision 5; the prototype's `.sx-on`): the brand
 * ring and a faint tint, distinct from a peek's outline, which keeps the
 * card's own background.
 */
export const PICKED = "border-brand shadow-[0_0_0_1px_var(--brand)] bg-[image:linear-gradient(var(--brand-tint),var(--brand-tint))]";

/**
 * The card's button's own handlers, around its peek's: Ctrl, ⌘ or Shift with
 * a click selects; ☰ or Shift+F10 opens its menu, ending a press under way.
 */
export function boardHandlers(
  board: CardBoard | undefined,
  peek: { button: { onClick: () => void; onKeyDown: (event: KeyboardEvent<HTMLElement>) => void }; cancel: () => void },
) {
  return {
    "data-card-key": board?.key,
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      if (board?.onPickClick(event) === true) {
        return;
      }
      peek.button.onClick();
    },
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (board !== undefined && isContextMenuKey(event)) {
        event.preventDefault();
        peek.cancel();
        board.onMenuKey(event.currentTarget);
        return;
      }
      peek.button.onKeyDown(event);
    },
    onMouseDown: (event: MouseEvent<HTMLButtonElement>) => {
      // Shift+click selects a range: not the page's text between.
      if (board !== undefined && event.shiftKey && event.button === 0) {
        event.preventDefault();
      }
    },
  };
}

/** The card's list item's right-click: its menu, from the card's button. */
export function onCardContextMenu(board: CardBoard | undefined) {
  return board === undefined
    ? undefined
    : (event: MouseEvent<HTMLElement>) => {
        board.onContextMenu(event, event.currentTarget.querySelector<HTMLButtonElement>("button[data-card-key]"));
      };
}
